import React, { useState, useEffect, useCallback } from 'react'
import { CButton, CCard, CCardTitle, CImage } from '@coreui/react-pro'
import './DiagnosisCard.scss'
import { useSelector } from 'react-redux'
import Select from 'react-select'
import PropTypes from 'prop-types'
import tick from '../../assets/icons/Encounter-Progress.svg'
import aiTick from '../../assets/icons/Encounter-ProgressAI.svg'
import { useDispatch } from 'react-redux'
import CardDiagnosisProcedure from '../common/CardDiagnosisProcedure/CardDiagnosisProcedure'
import CardListProcedureDiagnosis from '../common/CardListProcedureDiagnosis/CardListProcedureDiagnosis'
import { getFavDiagnosis } from 'src/helper/backendHelper'
import { userSettingGet, userSettingPut } from 'src/redux/user/userThunk'
import PullFromFacility from '../common/PullFromFacility/PullFromFacility'
import _ from 'lodash'
import {
  sortDxByFilterType,
  sortDxByFavorites,
  diagnosisSearch,
} from '../common/shared/sharedDXLogic'
import Loader from '../common/Loader/Loader'
import CodeSearchBox from '../common/SearchBoxEncounter/CodeSearchBox'
import PdpmAiModal from './PdpmAiModal'
import FEATURES from '../../utils/featuresConstants'
import { selectStyleDark, selectStyleLight } from '../../utils/sharedCareTeamLogic'
import { getTheme } from '../../redux/user/userSlice'
import { getIsAi } from '../../utils/sharedFeaturesFunctions'

//todo
function evaluateDXsToShow(
  searchText,
  searchResults,
  favorites,
  currentCodeTemplate,
  currentCodeTemplateResults,
) {
  if (searchText) {
    return searchResults
  } else if (currentCodeTemplate === 'Favorites') {
    return Object.values(favorites)
  } else if (currentCodeTemplate) {
    return currentCodeTemplateResults
  } else {
    // no text or filter -> default to favorites
    return Object.values(favorites)
  }
}

async function fetchDetailedTemplateData(currentCodeTemplate, callback) {
  // if favorites, we already have them locally, so don't call for them
  if (currentCodeTemplate === 'Favorites') {
    return callback([])
  }

  await getFavDiagnosis({
    template: currentCodeTemplate,
  }).then((res) => {
    if (res?.status === 'success') {
      return callback(res.results)
    }
    return callback([])
  })
}

const dxOption = [
  { label: 'Code', value: 'code' },
  { label: 'Description', value: 'description' },
  { label: 'Usage', value: '~count,code' },
]

const DiagnosisCard = ({
  setSelectedDiagnosis,
  selectedDiagnosis,
  visitId,
  noIndicater,
  isProgressNote,
  title,
  isBillSummary,
  patientId,
  isPcc,
  dateOfService,
  showDescription,
  onOptionChange,
  shouldFocus = true,
  showPDPM = true,
}) => {
  const dispatch = useDispatch()
  const settings = useSelector((state) => state.user.settings) || []
  const features = useSelector((state) => state.user.features) || []
  const provFavDX = useSelector((state) => state.user?.providerFavDX)
  const theme = useSelector(getTheme)

  const [searchText, setSearchText] = useState('')
  const [searchResult, setSearchResult] = useState([])
  const [currentCodeTemplate, setCurrentCodeTemplate] = useState(null)
  const [currentCodeTemplateResults, setCurrentCodeTemplateResults] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const hasAiFlag = features?.includes(FEATURES.DesktopAI)

  let displayedDxs = evaluateDXsToShow(
    searchText,
    searchResult,
    provFavDX,
    currentCodeTemplate,
    currentCodeTemplateResults,
  )

  // if there is search text, rely on DX search results
  // and don't manually filter anything
  let filterSortedDx = searchText
    ? displayedDxs
    : sortDxByFilterType(displayedDxs, settings?.sortDx)
  displayedDxs = sortDxByFavorites(filterSortedDx, provFavDX)

  const diagnosisSelected = selectedDiagnosis?.length > 0

  const diagnosisSearchCallback = useCallback(
    _.debounce((searchText, provFavDX) => {
      setIsLoading(true)
      searchData(searchText, provFavDX)
    }, 700),
    [settings?.sortDx],
  )

  const searchData = async (text, provFavDX) => {
    const res = await diagnosisSearch({
      searchString: text,
    })

    setIsLoading(false)
    setSearchResult(res || [])
  }

  useEffect(() => {
    diagnosisSearchCallback(searchText, provFavDX)
  }, [searchText, diagnosisSearchCallback])

  const handleAdd = (item) => {
    if (selectedDiagnosis.findIndex((diagnosis) => diagnosis.code === item.code) === -1) {
      setSelectedDiagnosis([...selectedDiagnosis, item])
    }
    if (onOptionChange) {
      onOptionChange(item, selectedDiagnosis.length)
    }
  }

  const onDragStart = (ev, datacodeDiagnosis) => {
    ev.dataTransfer.setData('datacodeDiagnosis', datacodeDiagnosis)
  }

  const onDragOver = (ev) => {
    ev.preventDefault()
  }

  const onDrop = (ev) => {
    let datacodeDiagnosis = ev.dataTransfer.getData('datacodeDiagnosis')
    let newDiagnosis = displayedDxs?.find((diagnosis) => diagnosis.code === datacodeDiagnosis)

    if (selectedDiagnosis.findIndex((diagnosis) => diagnosis.code === newDiagnosis.code) === -1) {
      setSelectedDiagnosis([...selectedDiagnosis, newDiagnosis])
    }
  }

  return (
    <CCard className={'diagnosisCard'}>
      {isLoading && <Loader />}
      <div className="diagnosisCardHeader">
        <div className="diagnosisHeaderContainer">
          <div className="diagnosisHeader">
            {!noIndicater && (
              <>
                {diagnosisSelected ? (
                  <CImage className="tick-size-diag" src={getIsAi() ? aiTick : tick} />
                ) : (
                  <span className="encounter-progress-number nonactive-number-progress-encounter"></span>
                )}
              </>
            )}

            <CCardTitle className="diagnosisCardTitle">{title ?? 'Diagnosis Codes'}</CCardTitle>
          </div>
          {!noIndicater && (
            <>
              {diagnosisSelected && (
                <span className="completed-container completed-text">COMPLETED</span>
              )}
            </>
          )}
        </div>
      </div>

      <div
        className="row mt-2"
        style={{ marginLeft: '0', marginRight: '0', width: !isBillSummary ? '100%' : '95%' }}
      >
        <div
          className="col-3"
          style={{
            width: '50%',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Select
            style={{
              visibility: searchText ? 'hidden' : 'visible',
            }}
            menuShouldScrollIntoView={false}
            className="dxSelectList"
            styles={theme === 'light' ? selectStyleLight : selectStyleDark}
            onChange={async ({ value }) => {
              if (value !== 'undefined' && value !== settings?.sortDx) {
                await dispatch(
                  userSettingPut({
                    ...settings,
                    sortDx: value,
                  }),
                )
                await dispatch(userSettingGet())
              }
            }}
            options={dxOption}
            value={dxOption?.find((opt) => opt.value === settings?.sortDx)}
          />
        </div>
        <div
          className="col-3"
          style={{
            width: '50%',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '16px',
          }}
        >
          {selectedDiagnosis?.length > 0 && (
            <CButton
              color="secondary"
              size="sm"
              variant="outline"
              shape="rounded-pill"
              style={{ padding: '0 16px' }}
              onClick={() => {
                setSelectedDiagnosis([])
              }}
            >
              Remove All
            </CButton>
          )}
          {isPcc && visitId && (
            <PullFromFacility
              patientId={patientId}
              visitId={visitId}
              dos={dateOfService}
              setSelectedDiagnosis={setSelectedDiagnosis}
            />
          )}
        </div>
      </div>

      <div className="diagnosis-card-search">
        <div className="column1 searchBox">
          <CodeSearchBox
            searchText={searchText}
            setSearchText={setSearchText}
            shouldFocus={shouldFocus}
            currentCodeTemplate={currentCodeTemplate}
            setCurrentCodeTemplate={(newTemplate) => {
              if (newTemplate) {
                fetchDetailedTemplateData(newTemplate, setCurrentCodeTemplateResults)
              }
              setCurrentCodeTemplate(newTemplate)
            }}
            type={'diagnosis'}
          />
        </div>
        <div
          className={
            isProgressNote
              ? 'diagnosis-card-select column2-progress'
              : 'diagnosis-card-select column2'
          }
        >
          Selected diagnosis ({selectedDiagnosis?.length})
          {hasAiFlag && showPDPM && (
            <PdpmAiModal dxCodes={selectedDiagnosis} setSelectedDiagnosis={setSelectedDiagnosis} />
          )}
        </div>
      </div>

      <div className="diagnosis-card-select-card">
        {isLoading === false && displayedDxs?.length > 0 ? (
          <div className="diagnosis-card-scroll column1">
            {displayedDxs.map((resultDx, index) => (
              <div key={index} onDragStart={(e) => onDragStart(e, resultDx.code)} draggable>
                <CardDiagnosisProcedure
                  item={resultDx}
                  index={index}
                  handleAddRemove={handleAdd}
                  icon={
                    selectedDiagnosis?.find((sel) => sel.code === resultDx.code) ? 'minus' : 'plus'
                  }
                  toggleFavorite={true}
                  selectedList={selectedDiagnosis}
                  type={'DIAGNOSIS'}
                  isBillSummary={isBillSummary}
                  showDescription={showDescription}
                />
              </div>
            ))}
          </div>
        ) : (
          <div id="noResults">
            <p>No results found</p>
          </div>
        )}
        <div
          className={
            isProgressNote
              ? 'diagnosis-card-scroll-progress column2-progress'
              : 'diagnosis-card-scroll column2'
          }
          onDragOver={(e) => onDragOver(e)}
          onDrop={(e) => onDrop(e)}
        >
          <CardListProcedureDiagnosis
            selectedList={selectedDiagnosis}
            setSelectedDiagnosis={setSelectedDiagnosis}
            type={'DIAGNOSIS'}
            isProgressNote={isProgressNote}
            serviceDate={dateOfService}
            showDescription={showDescription}
          />
        </div>
      </div>
    </CCard>
  )
}

DiagnosisCard.propTypes = {
  setSelectedDiagnosis: PropTypes.any,
  selectedDiagnosis: PropTypes.any,
}

export default DiagnosisCard
